import { apiClient } from '@/lib/apiClient';
import type {
  AgentListItem,
  AssignTicketResponse,
  ChangeStatusResponse,
  CreateTicketInput,
  CreateTicketResponse,
  DirectoryUser,
  MyTicketsListItem,
  PostMessageInput,
  QueueFilters,
  QueueTicketListItem,
  TicketCategory,
  TicketDetail,
  TicketMessage,
  TicketPriority,
  TicketStatus
} from './schema';

/**
 * Thin per-feature wrapper around apiClient. Centralising the endpoint paths
 * here means routes/components stay shape-agnostic — they call a typed
 * function, not a stringly-typed fetch.
 */
export const ticketsApi = {
  /** GET /api/tickets/mine — list the current user's tickets, newest-first. */
  listMine: (
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<MyTicketsListItem[]> =>
    apiClient.get<MyTicketsListItem[]>('/api/tickets/mine', { getAccessToken, signal }),

  /** GET /api/tickets/queue — all tickets (IT only), with optional filters. */
  listQueue: (
    filters: QueueFilters,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<QueueTicketListItem[]> => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.assignedToMe) params.set('assignedToMe', 'true');
    const qs = params.toString();
    return apiClient.get<QueueTicketListItem[]>(`/api/tickets/queue${qs ? `?${qs}` : ''}`, { getAccessToken, signal });
  },

  /** GET /api/tickets/{ticketNumber} — full ticket with thread. */
  getDetail: (
    ticketNumber: number,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<TicketDetail> =>
    apiClient.get<TicketDetail>(`/api/tickets/${ticketNumber}`, { getAccessToken, signal }),

  /** POST /api/tickets — submit a new ticket as the current user (with optional staged attachments + rich body). */
  create: (
    input: CreateTicketInput,
    getAccessToken: () => Promise<string>,
    extra?: { attachmentIds?: string[]; bodyJson?: string; bodyHtml?: string },
    signal?: AbortSignal
  ): Promise<CreateTicketResponse> =>
    apiClient.post<CreateTicketResponse>('/api/tickets', { ...input, ...extra }, { getAccessToken, signal }),

  /** POST /api/tickets/{ticketId}/messages — append a message to a ticket's thread. */
  postMessage: (
    ticketId: string,
    input: PostMessageInput,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<TicketMessage> =>
    apiClient.post<TicketMessage>(`/api/tickets/${ticketId}/messages`, input, { getAccessToken, signal }),

  /** GET /api/users/agents — assignable IT users (IT only). */
  listAgents: (
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<AgentListItem[]> =>
    apiClient.get<AgentListItem[]>('/api/users/agents', { getAccessToken, signal }),

  /** PUT /api/tickets/{ticketId}/assignee — assign (or unassign with null) a ticket (IT only). */
  assignTicket: (
    ticketId: string,
    assigneeId: string | null,
    getAccessToken: () => Promise<string>
  ): Promise<AssignTicketResponse> =>
    apiClient.put<AssignTicketResponse>(`/api/tickets/${ticketId}/assignee`, { assigneeId }, { getAccessToken }),

  /**
   * PUT /api/tickets/{ticketId}/status — change a ticket's status (IT only).
   * `vanish` suppresses the requester's close email when moving to Completed.
   */
  changeStatus: (
    ticketId: string,
    status: TicketStatus,
    getAccessToken: () => Promise<string>,
    vanish = false
  ): Promise<ChangeStatusResponse> =>
    apiClient.put<ChangeStatusResponse>(`/api/tickets/${ticketId}/status`, { status, vanish }, { getAccessToken }),

  /** PUT /api/tickets/{ticketId}/auto-close — arm/disarm "Close if no response" (IT only). */
  setAutoClose: (
    ticketId: string,
    armed: boolean,
    getAccessToken: () => Promise<string>
  ): Promise<{ armed: boolean; dueAt: string | null }> =>
    apiClient.put<{ armed: boolean; dueAt: string | null }>(
      `/api/tickets/${ticketId}/auto-close`,
      { armed },
      { getAccessToken }
    ),

  /** PUT /api/tickets/{ticketId}/classification — change category/application/priority (IT only). */
  reclassify: (
    ticketId: string,
    input: { category: TicketCategory; application: string | null; priority: TicketPriority },
    getAccessToken: () => Promise<string>
  ): Promise<{ category: TicketCategory; application: string | null; priority: TicketPriority }> =>
    apiClient.put(`/api/tickets/${ticketId}/classification`, input, { getAccessToken }),

  /** DELETE /api/tickets/{ticketId} — permanently delete a ticket (IT Admin). */
  deleteTicket: (ticketId: string, getAccessToken: () => Promise<string>): Promise<void> =>
    apiClient.del<void>(`/api/tickets/${ticketId}`, { getAccessToken }),

  /** POST /api/tickets/bulk-delete — delete multiple tickets (IT Admin). */
  bulkDeleteTickets: (
    ticketIds: string[],
    getAccessToken: () => Promise<string>
  ): Promise<{ deleted: number }> =>
    apiClient.post<{ deleted: number }>('/api/tickets/bulk-delete', { ticketIds }, { getAccessToken }),

  /** GET /api/directory/users?q= — search the company directory (watcher picker). */
  searchDirectory: (
    q: string,
    getAccessToken: () => Promise<string>,
    signal?: AbortSignal
  ): Promise<DirectoryUser[]> =>
    apiClient.get<DirectoryUser[]>(`/api/directory/users?q=${encodeURIComponent(q)}`, { getAccessToken, signal }),

  /** POST /api/tickets/{ticketId}/watchers — add a watcher by directory oid. */
  addWatcher: (
    ticketId: string,
    oid: string,
    getAccessToken: () => Promise<string>
  ): Promise<void> =>
    apiClient.post<void>(`/api/tickets/${ticketId}/watchers`, { oid }, { getAccessToken }),

  /** DELETE /api/tickets/{ticketId}/watchers/{userId} — remove a watcher. */
  removeWatcher: (
    ticketId: string,
    userId: string,
    getAccessToken: () => Promise<string>
  ): Promise<void> =>
    apiClient.del<void>(`/api/tickets/${ticketId}/watchers/${userId}`, { getAccessToken })
};
